/**
 * Main application component with routing
 *
 * @author Claude
 */
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { Layout } from './components/layout/Layout';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { AdminRoute } from './components/common/AdminRoute';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './components/common/ErrorFallback';
import FocusManager from './components/common/FocusManager';
import KeyboardNavigationProvider from './components/common/KeyboardNavigationProvider';
import { RateLimitProvider } from './context/RateLimitContext';
import RateLimitIndicator from './components/RateLimitIndicator';
import { NotificationProvider } from './contexts/NotificationContext';
import LoadingIndicator from './components/common/LoadingIndicator';

// Import i18n configuration
import './i18n';

// Import RTL styles
import './styles/rtl.css';

// Import Logging Context
import { LoggingProvider } from './contexts/LoggingContext';

// Eager load critical components that are likely to be needed immediately
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';

// Lazy load authentication pages
const Register = React.lazy(() => import('./pages/auth/Register'));
const AcceptInvite = React.lazy(() => import('./pages/auth/AcceptInvite'));
const EmailVerification = React.lazy(() => import('./pages/auth/EmailVerification'));
const ForgotPassword = React.lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));

// Lazy load user pages
const UserProfile = React.lazy(() =>
  import('./pages/user/UserProfile').then(module => ({ default: module.UserProfile }))
);
const UserSettings = React.lazy(() =>
  import('./pages/user/UserSettings').then(module => ({ default: module.UserSettings }))
);
const ChangePassword = React.lazy(() =>
  import('./pages/user/ChangePassword').then(module => ({ default: module.ChangePassword }))
);
const DeleteAccount = React.lazy(() =>
  import('./pages/user/DeleteAccount').then(module => ({ default: module.DeleteAccount }))
);
const MyAuditLogs = React.lazy(() =>
  import('./pages/user/MyAuditLogs').then(module => ({ default: module.MyAuditLogs }))
);
const ExportData = React.lazy(() =>
  import('./pages/user/ExportData').then(module => ({ default: module.ExportData }))
);
const ConsentPreferences = React.lazy(() =>
  import('./pages/user/ConsentPreferences').then(module => ({ default: module.ConsentPreferences }))
);

// Lazy load admin pages
const AuditLogs = React.lazy(() =>
  import('./pages/admin/AuditLogs').then(module => ({ default: module.AuditLogs }))
);

// Lazy load all other page components for better code splitting
const ClientDetail = React.lazy(() => import('./pages/ClientDetail'));
const CreateClient = React.lazy(() => import('./pages/CreateClient'));
const EditClient = React.lazy(() => import('./pages/EditClient'));

const Dimensions = React.lazy(() => import('./pages/Dimensions'));
const DimensionDetail = React.lazy(() => import('./pages/DimensionDetail'));
const CreateDimension = React.lazy(() => import('./pages/CreateDimension'));
const EditDimension = React.lazy(() => import('./pages/EditDimension'));

const ProofPoints = React.lazy(() => import('./pages/ProofPoints'));
const ProofPointDetail = React.lazy(() => import('./pages/ProofPointDetail'));
const NewProofPoint = React.lazy(() => import('./pages/NewProofPoint'));
const EditProofPoint = React.lazy(() => import('./pages/EditProofPoint'));

const Criteria = React.lazy(() => import('./pages/Criteria'));
const CriteriaDetail = React.lazy(() => import('./pages/CriteriaDetail'));
const NewCriteria = React.lazy(() => import('./pages/NewCriteria'));
const EditCriteria = React.lazy(() => import('./pages/EditCriteria'));

const ClientReports = React.lazy(() => import('./pages/ClientReports'));
const ReportDetail = React.lazy(() => import('./pages/ReportDetail'));
const NewReport = React.lazy(() => import('./pages/NewReport'));
const EditReport = React.lazy(() => import('./pages/EditReport'));

const ReportData = React.lazy(() => import('./pages/ReportData'));
const ReportDataDetail = React.lazy(() => import('./pages/ReportDataDetail'));
const NewReportData = React.lazy(() => import('./pages/NewReportData'));
const EditReportData = React.lazy(() => import('./pages/EditReportData'));

const SearchResults = React.lazy(() => import('./pages/SearchResults'));

const Assessment = React.lazy(() =>
  import('./pages/Assessment').then(module => ({ default: module.Assessment }))
);
const AdminDashboard = React.lazy(() =>
  import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard }))
);
const ComparativeAnalysis = React.lazy(() => import('./pages/ComparativeAnalysis'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const AccessibilityStatement = React.lazy(() => import('./pages/AccessibilityStatement'));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 300000, // 5 minutes
    },
  },
});

/**
 * Root application component with routing
 * @returns {JSX.Element} App component
 *
 * @a11y Uses proper focus management with routing
 * @a11y Implements error boundary for graceful error handling
 */
const App = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <HelmetProvider>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <RateLimitProvider>
                <NotificationProvider>
                  <LoggingProvider>
                    <Router>
                      <RateLimitIndicator position="top" />
                      <FocusManager>
                        <KeyboardNavigationProvider>
                          <Suspense fallback={<LoadingIndicator message="Loading page..." />}>
                            <Routes>
                              {/* Public routes */}
                              <Route path="/login" element={<Login />} />
                              <Route path="/register" element={<Register />} />
                              <Route path="/accept-invite" element={<AcceptInvite />} />
                              <Route path="/verify-email" element={<EmailVerification />} />
                              <Route path="/forgot-password" element={<ForgotPassword />} />
                              <Route path="/reset-password" element={<ResetPassword />} />

                              {/* Protected routes - wrapped in Layout and ProtectedRoute */}
                              <Route
                                element={
                                  <ProtectedRoute>
                                    <Layout />
                                  </ProtectedRoute>
                                }
                              >
                                {/* Dashboard */}
                                <Route path="/dashboard" element={<Dashboard />} />

                                {/* Clients */}
                                <Route path="/clients">
                                  <Route index element={<Navigate to="/dashboard" replace />} />
                                  <Route path=":clientId" element={<ClientDetail />} />
                                  <Route path=":clientId/edit" element={<EditClient />} />
                                  <Route path="new" element={<CreateClient />} />
                                </Route>

                                {/* Reports */}
                                <Route path="/clients/:clientId/reports">
                                  <Route index element={<ClientReports />} />
                                  <Route path=":reportId" element={<ReportDetail />} />
                                  <Route path=":reportId/edit" element={<EditReport />} />
                                  <Route path="new" element={<NewReport />} />
                                </Route>

                                {/* Report Data */}
                                <Route path="/clients/:clientId/reports/:reportId/data">
                                  <Route index element={<ReportData />} />
                                  <Route path=":reportDataId" element={<ReportDataDetail />} />
                                  <Route path=":reportDataId/edit" element={<EditReportData />} />
                                  <Route path="new" element={<NewReportData />} />
                                </Route>

                                {/* Dimensions (admin only) */}
                                <Route path="/dimensions">
                                  <Route
                                    index
                                    element={
                                      <AdminRoute>
                                        <Dimensions />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path=":dimensionId"
                                    element={
                                      <AdminRoute>
                                        <DimensionDetail />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path=":dimensionId/edit"
                                    element={
                                      <AdminRoute>
                                        <EditDimension />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path="new"
                                    element={
                                      <AdminRoute>
                                        <CreateDimension />
                                      </AdminRoute>
                                    }
                                  />
                                </Route>

                                {/* Proof Points (admin only) */}
                                <Route path="/dimensions/:dimensionId/proofpoints">
                                  <Route
                                    index
                                    element={
                                      <AdminRoute>
                                        <ProofPoints />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path=":proofPointId"
                                    element={
                                      <AdminRoute>
                                        <ProofPointDetail />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path=":proofPointId/edit"
                                    element={
                                      <AdminRoute>
                                        <EditProofPoint />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path="new"
                                    element={
                                      <AdminRoute>
                                        <NewProofPoint />
                                      </AdminRoute>
                                    }
                                  />
                                </Route>

                                {/* Proof Point Criteria (admin only) */}
                                <Route path="/dimensions/:dimensionId/proofpoints/:proofPointId/criteria">
                                  <Route
                                    index
                                    element={
                                      <AdminRoute>
                                        <Criteria />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path=":criteriaId"
                                    element={
                                      <AdminRoute>
                                        <CriteriaDetail />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path=":criteriaId/edit"
                                    element={
                                      <AdminRoute>
                                        <EditCriteria />
                                      </AdminRoute>
                                    }
                                  />
                                  <Route
                                    path="new"
                                    element={
                                      <AdminRoute>
                                        <NewCriteria />
                                      </AdminRoute>
                                    }
                                  />
                                </Route>

                                {/* Assessment */}
                                <Route
                                  path="/clients/:clientId/reports/:reportId/assessment"
                                  element={<Assessment />}
                                />

                                {/* Comparative Analysis */}
                                <Route
                                  path="/comparative-analysis"
                                  element={<ComparativeAnalysis />}
                                />

                                {/* Admin (admin only) */}
                                <Route
                                  path="/admin"
                                  element={
                                    <AdminRoute>
                                      <AdminDashboard />
                                    </AdminRoute>
                                  }
                                />
                                <Route
                                  path="/admin/audit-logs"
                                  element={
                                    <AdminRoute>
                                      <AuditLogs />
                                    </AdminRoute>
                                  }
                                />

                                {/* User Management */}
                                <Route path="/user/profile" element={<UserProfile />} />
                                <Route path="/user/settings" element={<UserSettings />} />
                                <Route path="/user/change-password" element={<ChangePassword />} />
                                <Route path="/user/delete-account" element={<DeleteAccount />} />
                                <Route path="/user/activity" element={<MyAuditLogs />} />
                                <Route path="/user/export-data" element={<ExportData />} />
                                <Route path="/user/consent" element={<ConsentPreferences />} />
                              </Route>

                              {/* Public static pages */}
                              <Route
                                path="/privacy"
                                element={
                                  <Layout>
                                    <PrivacyPolicy />
                                  </Layout>
                                }
                              />
                              <Route
                                path="/terms"
                                element={
                                  <Layout>
                                    <TermsOfService />
                                  </Layout>
                                }
                              />
                              <Route
                                path="/accessibility"
                                element={
                                  <Layout>
                                    <AccessibilityStatement />
                                  </Layout>
                                }
                              />

                              {/* Default redirect */}
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />

                              {/* 404 route */}
                              <Route
                                path="*"
                                element={
                                  <Layout>
                                    <div>Page not found</div>
                                  </Layout>
                                }
                              />
                            </Routes>
                          </Suspense>
                        </KeyboardNavigationProvider>
                      </FocusManager>
                    </Router>
                  </LoggingProvider>
                </NotificationProvider>
              </RateLimitProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
